/**
 * Standardfilter der Auswertungen.
 *
 * Zwei Entscheidungen, die ohne Zutun gelten und bewusst so herum:
 *  * Kostenlose Lizenzen sind AUSGEBLENDET. Sie machen bei Microsoft 365 einen
 *    erheblichen Teil der Stueckzahlen aus (Power BI Free, Power Automate
 *    Free, Testversionen, Ressourcenkonten) und verzerren Auslastung und
 *    Kostensicht.
 *  * Deaktivierte Konten sind AUSGEBLENDET. Gefragt ist der Bedarf aktiver
 *    Konten. Wichtig zur Einordnung: Microsoft berechnet eine Lizenz weiter,
 *    solange sie zugewiesen ist – auch bei gesperrter Anmeldung. Die
 *    Differenz bleibt daher als Kennzahl "Nicht genutzt" und als
 *    "Kosten deaktivierte Konten" sichtbar; sie ist Einsparpotenzial, keine
 *    bereits erzielte Ersparnis.
 *
 * Darstellungsentscheidungen, keine Datenaenderung – daher lokal im Browser.
 * Der Server filtert trotzdem, damit Summen, Diagramme und Exporte dieselbe
 * Grundmenge sehen.
 */
import { useCallback, useState } from 'react';

const KEY_FREE = 'audit.includeFree';
const KEY_DISABLED = 'audit.includeDisabled';

function readStored(key: string): boolean {
  try {
    // Nur ein ausdrueckliches "true" schaltet ein; fehlt der Eintrag, gilt der
    // Standard – also ausgeblendet.
    return window.localStorage.getItem(key) === 'true';
  } catch {
    return false;
  }
}

function store(key: string, value: boolean): void {
  try {
    window.localStorage.setItem(key, String(value));
  } catch {
    // Persistenz ist optional; der Schalter wirkt trotzdem fuer die Sitzung.
  }
}

export interface ReportDefaults {
  includeFree: boolean;
  includeDisabled: boolean;
  /** Direkt als Anfrageparameter verwendbar. */
  excludeFree: boolean;
  excludeDisabled: boolean;
  setIncludeFree: (next: boolean) => void;
  setIncludeDisabled: (next: boolean) => void;
}

export function useReportDefaults(): ReportDefaults {
  const [includeFree, setFree] = useState<boolean>(() => readStored(KEY_FREE));
  const [includeDisabled, setDisabled] = useState<boolean>(() => readStored(KEY_DISABLED));

  const setIncludeFree = useCallback((next: boolean) => {
    setFree(next);
    store(KEY_FREE, next);
  }, []);
  const setIncludeDisabled = useCallback((next: boolean) => {
    setDisabled(next);
    store(KEY_DISABLED, next);
  }, []);

  return {
    includeFree,
    includeDisabled,
    excludeFree: !includeFree,
    excludeDisabled: !includeDisabled,
    setIncludeFree,
    setIncludeDisabled,
  };
}
